'use client';

/**
 * ChatContext — central provider for AI assistant state.
 *
 * Composes all extracted hooks and exposes a unified API.
 * The connection machine driver (useAssistantConnection) consumes
 * this context to dispatch server events to the appropriate hooks, and
 * registers its dispatch on connectionDispatchRef so the intent actions
 * here (sendText / retryMessage / startNewChat) can feed the machine.
 *
 * Consumers use `useChatContext()` to access state and actions.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ConnectionStatus,
  UIState,
  VizMode,
  WSEventType,
  type CallNotice,
  type Message,
  type ContactField,
  type HandoffContext,
  type HandoffEndReason,
  type ChatLayoutRef,
  type PromptInputBarRef,
  type TurnState,
  type ToolTimeoutOfferState,
  type ToolRetryOutcomeState,
} from '@/types/chat';
import { useMessageManagement } from '@/hooks/useMessageManagement';
import type {
  AgentMessageInsert,
  ContactSummaryInsert,
} from '@/hooks/useMessageManagement';
import { useThinkingTicker } from '@/hooks/useThinkingTicker';
import { useAudioVisualization } from '@/hooks/useAudioVisualization';
import type { AudioLevels } from '@/hooks/useAudioVisualization';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';
import { useCallMode } from '@/hooks/useCallMode';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';
import { useHandoff } from '@/hooks/useHandoff';
import { usePinnedCard } from '@/hooks/usePinnedCard';
import {
  useToolTimeoutFlow,
  type ToolTimeoutFlowContextValue,
} from '@/hooks/useToolTimeoutFlow';
import type { CallFailureReason, CallPhase } from '@/lib/call-phase';
import { settleAbandonedTurn } from '@/lib/conversation-status';
import { useConversationStore } from '@/stores/conversation-store';
import { DEFAULT_HEADLINE } from '@/lib/assistant-topics';
import type { PinnedCard } from '@/lib/pin-routing';
import type { PinEntry } from '@/lib/pin-history';
import { v4 as uuidv4 } from 'uuid';
import { resolveSendFrame } from '@/machines/connection/send-routing';
import { OutboundFrameKind, type ConnEvent } from '@/machines/connection/types';
import { resolveEffectiveToolTimeoutOffer } from '@/lib/tool-timeout-restore';
import { resolveKnownContact } from '@/lib/known-contact';

// ─── Context shape ──────────────────────────────────────────────

export interface ChatContextValue {
  // Messages
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  insertAssistantMessage: (itemId: string | undefined, content: string) => void;
  insertToolResult: (
    toolName: string,
    payload: unknown,
    itemId?: string,
    searchedLocation?: string
  ) => void;
  /** Insert a HUMAN_AGENT message (HubSpot rep reply, relayed live). Dedup'd
   *  on HubSpot's message_id — see useMessageManagement. */
  insertHumanAgentMessage: (event: AgentMessageInsert) => void;
  /** Insert the post-submit "Details shared" summary card on the live
   *  CONTACT_SUMMARY event (Decision #18.1,
   *  2026-07-hubspot-native-handoff.md). Dedup'd on handoff_id — see
   *  useMessageManagement. */
  insertContactSummary: (event: ContactSummaryInsert) => void;
  /** Accumulate a streamed text token into the message keyed by item_id.
   *  `responseId` tags the message so a later msg_class can classify it.
   *  Used for BOTH text-mode deltas and in-call transcript_delta frames —
   *  one streaming pipeline (see useAssistantConnection's TRANSCRIPT_DELTA
   *  handler). */
  appendAssistantDelta: (
    itemId: string,
    delta: string,
    responseId?: string
  ) => void;
  /** Drop the stream buffer for one item id, without touching any other
   *  buffer or `messages`. Called by the connection driver's HISTORY_UPDATED
   *  reconcile right after an authoritative snapshot replaces a
   *  still-streaming message, so a stray late delta for that id can't
   *  resurrect stale accumulated text. See useMessageManagement.ts. */
  dropStreamBuffer: (itemId: string) => void;
  /** Drop stream buffers + clear any lingering caret (turn end / reconnect). */
  finalizeStreaming: () => void;
  /** Apply the backend commentary/answer classification for a whole responseId. */
  classifyMessages: (responseId: string, isFinal: boolean) => void;
  /** Message-persisted id bridge: stamp the server-minted `messages.id` onto
   *  the live message keyed by `item_id` once WSEventType.MESSAGE_PERSISTED
   *  confirms the row is durably written. See useMessageManagement.ts. */
  setMessageDbId: (itemId: string, dbId: string) => void;

  // UI state
  uiState: UIState;
  setUIState: (state: UIState) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  connectionStatus: ConnectionStatus;
  setConnectionStatus: (status: ConnectionStatus) => void;
  /** Backend-authoritative turn activity (the TURN_STATE event), or null when
   *  no signal exists this connection — null doubles as the old-backend
   *  capability gate (the UI falls back to message-shape derivation). Set by
   *  the connection driver; cleared on idle/socket-close/reset. */
  turnState: TurnState | null;
  setTurnState: (state: TurnState | null) => void;
  /** Landing-screen hero headline. Lives here (not page-local) so
   *  startNewChat() can reset it — a topic click mutates it and, before the
   *  lift, a "new chat" kept showing the previous topic's headline. */
  headline: string;
  setHeadline: (headline: string) => void;

  // Live-agent handoff (see src/hooks/useHandoff.ts). Null when no handoff
  // is active this session. Driven entirely by server events, dispatched
  // from useAssistantConnection's handleServerEvent.
  handoff: HandoffContext | null;
  /** contact_info_requested (Decision #17, 2026-07-hubspot-native-handoff.md)
   *  — enters/replaces the pre-handoff contact-collection (COLLECTING)
   *  phase. See the CONTACT_INFO_REQUESTED case in useAssistantConnection.ts
   *  and src/hooks/useHandoff.ts. */
  contactInfoRequested: (handoffId: string, fields: ContactField[]) => void;
  startHandoff: (handoffId: string, domain: HandoffContext['domain']) => void;
  agentJoined: (agentDisplayName: string) => void;
  agentMessageReceived: (agentDisplayName: string) => void;
  /** handoff_ended → phase ENDED. Threads the server's end_reason through
   *  (see HandoffContext.endReason) rather than dropping it. */
  markHandoffEnded: (endReason?: HandoffEndReason) => void;
  /** handoff_timeout → phase TIMED_OUT (its own phase — see HandoffPhase's
   *  JSDoc in types/chat.ts for why this isn't folded into markHandoffEnded). */
  markHandoffTimedOut: () => void;
  clearHandoff: () => void;
  /** User-initiated end (the "End chat" affordance). Sends END_HANDOFF
   *  directly on the socket — a one-shot control frame, no ack/retry
   *  lifecycle needed (same "bypasses the outbox" precedent as call audio on
   *  wsRef). The actual phase→ENDED transition happens when the server
   *  confirms via HANDOFF_ENDED, not optimistically here. Also doubles as the
   *  Cancel affordance on ContactInfoCard during COLLECTING (Decision #17) —
   *  the backend ends a still-`pending_contact` row locally; no remote
   *  HubSpot close is needed since no thread was ever created for it. */
  sendEndHandoff: () => void;
  /** Submit the pre-handoff contact form (Decision #17). One-shot control
   *  frame sent directly on the socket — same "bypasses the outbox"
   *  precedent as sendEndHandoff above: this is a form submission, not a
   *  chat message, so it has no Message bubble / SendStatus to drive through
   *  the ack-retry outbox built for text turns. The response is either
   *  HANDOFF_STARTED (accepted — COLLECTING exits to WAITING) or a fresh
   *  CONTACT_INFO_REQUESTED (server-side validation failed — the card
   *  re-renders with corrected guidance); ContactInfoCard's own submitting
   *  flag resets on either. */
  submitContactInfo: (values: {
    name: string;
    email: string;
    phone?: string;
  }) => void;

  // Session-recovery: tool-timeout offer (a backend MCP tool call — course
  // search / content lookup — timed out; see ToolTimeoutCard,
  // tool-timeout-*.tsx). Live-only state, set from the TOOL_TIMEOUT_OFFER WS
  // event (useAssistantConnection.ts) — null when no tool has timed out this
  // session, or before the first one has. Mirrors `turnState` above: a
  // plain nullable object + setter, no dedicated hook, since (unlike
  // `handoff`) there's no multi-phase transition to encapsulate here — the
  // container (ToolTimeoutCard) owns all of that via lib/tool-timeout-view.ts.
  toolTimeoutOffer: ToolTimeoutOfferState | null;
  setToolTimeoutOffer: (offer: ToolTimeoutOfferState | null) => void;
  /** The explicit outcome of ONE retry_tool_call attempt (session-recovery
   *  retry_id-correlation fix) — set from the TOOL_RETRY_OUTCOME WS event.
   *  LIVE-ONLY (never persisted/restored, unlike toolTimeoutOffer above).
   *  Mirrors toolTimeoutOffer's shape exactly; useToolTimeoutFlow is the
   *  only real consumer (via the `toolTimeoutFlow` field below) — exposed
   *  here mainly for symmetry/testability. */
  toolRetryOutcome: ToolRetryOutcomeState | null;
  setToolRetryOutcome: (outcome: ToolRetryOutcomeState | null) => void;
  /** The "Try again" chip on a tool_timeout_offer card. One-shot control
   *  frame sent directly on the socket — same "bypasses the outbox"
   *  precedent as sendEndHandoff/submitContactInfo above: this re-drives an
   *  already-in-progress backend retry, not a chat turn, so it has no
   *  Message bubble / SendStatus to attach to the ack-retry outbox built for
   *  text turns. Takes the triggering offer's `retryId` (the backend
   *  rejects the frame outright if it doesn't match the pending incident)
   *  and returns whether the frame was actually SENT (the socket was open)
   *  — callers must not act as though a retry is in flight when this
   *  returns false (session-recovery Finding 5: a frame that never left the
   *  browser can never produce an outcome to resolve it). The eventual
   *  outcome itself arrives via `toolRetryOutcome` above, correlated by
   *  `retryId` — never inferred from `turnState`. */
  retryToolCall: (retryId: string) => boolean;
  /** The tool-timeout recovery flow's SHARED state (view machine, submit
   *  handlers, the resolved offer + on-file phone) — composed here, ONCE,
   *  via useToolTimeoutFlow (src/hooks/) rather than by each renderer, so
   *  the transcript's ToolTimeoutCard and the call surface's
   *  CallToolTimeoutPanel can never independently diverge on the SAME
   *  incident (the transcript stays mounted underneath the call overlay
   *  for the whole call — see useToolTimeoutFlow's own header comment for
   *  the exact divergence bug this fixes). null whenever no offer is
   *  outstanding (live or restored) this session — both renderers gate on
   *  this being non-null before rendering anything. */
  toolTimeoutFlow: ToolTimeoutFlowContextValue | null;

  // Visualization. Level itself is NOT context state — see
  // src/hooks/useAudioVisualization.ts: it lives on a mutable ref that
  // renderers read per-frame, specifically so audio-rate updates (~60Hz)
  // never flow through this context (that was the 20Hz-during-calls
  // re-render bug fixed alongside the useMemo below).
  vizMode: VizMode;
  setVizMode: (mode: VizMode) => void;
  /** Shared engine ref — `.current.{user,agent,active}`, 0..1. See
   *  useAudioVisualization.ts. */
  audioLevelsRef: React.RefObject<AudioLevels>;
  /** Snaps VizMode/levels back to IDLE immediately (call end, playback
   *  interrupt, tool-call end, reconnect) instead of waiting out the
   *  engine's natural activity-timeout decay. */
  resetToIdle: () => void;

  // Thinking
  isThinkingRef: React.MutableRefObject<boolean>;
  startThinkingTicker: () => Promise<void>;
  stopThinkingTicker: () => Promise<void>;

  // Audio playback
  enqueueAudio: (base64: string) => void;
  clearPlayback: () => void;

  // Call mode
  isCalling: boolean;
  isCallingRef: React.MutableRefObject<boolean>;
  /** [FB34] External-facing call-start phase — see src/lib/call-phase.ts's
   *  header for why every user-facing/external gate (composer,
   *  TypingIndicator, mounting CallScreen itself) reads THIS instead of
   *  `isCalling`: isCalling is still false throughout `connecting`, so it's
   *  not a safe axis for "is the call surface up" — the whole point of
   *  FB34 is that the surface mounts well before isCalling ever flips. */
  callPhase: CallPhase;
  /** Non-null only while `callPhase === 'failed'`. */
  callFailureReason: CallFailureReason | null;
  /** [FB34] True after ~10s in `connecting` with no resolution yet — getUserMedia
   *  has no timeout of its own, so this swaps the connecting copy to
   *  permission-aware wording instead of leaving the same text up forever.
   *  Never auto-fails the call. */
  waitingOnMicPermission: boolean;
  /** [FB34] The failed-phase "Try again" chip's entry point — tears down
   *  the previous partial attempt and starts a fresh one on the SAME
   *  transport. No-op outside `callPhase === 'failed'`. */
  retryCall: () => void;
  toggleCall: () => Promise<void>;
  stopCall: () => void;
  /** [FB34] Phase-aware stop for consumers OUTSIDE the call surface itself
   *  (this file's own cleanupResources, the connection machine's
   *  NOTIFY_TERMINAL/CONTACT_INFO_REQUESTED/HANDOFF_STARTED stops) — keys on
   *  `callPhase`, not `isCallingRef` (which misses `connecting`/`failed`).
   *  Returns whether a call was actually active (and thus stopped). Prefer
   *  this over the raw `stopCall` above for any external stop site. */
  stopCallIfActive: () => boolean;
  showCC: boolean;
  setShowCC: React.Dispatch<React.SetStateAction<boolean>>;
  isMuted: boolean;
  toggleMute: () => void;
  callStartedAtRef: React.MutableRefObject<number | null>;
  /** Connection-layer hooks (FB28): a live call SURVIVES a WebSocket drop —
   *  useAssistantConnection calls these from onclose/onopen so the call
   *  surface can show "Connection lost — reconnecting…" / "back online"
   *  instead of silently ending the call. No-ops outside a call. */
  onCallConnectionDrop: () => void;
  onCallConnectionRestored: () => void;
  /** Session-recovery (reconnect reset): useAssistantConnection calls this
   *  from `ws.onopen` on every open AFTER the first one — i.e. a genuine
   *  reconnect (recovery retry, or a mid-blip SESSION_CHANGED reopen),
   *  never the initial connect. A socket drop pops the backend's
   *  `pending_tool_retry[session_id]` entry in `_teardown_state` and never
   *  repopulates it on reconnect (verified backend-side) — that bookkeeping
   *  is a per-CONNECTION guarantee, not a per-incident one, even though the
   *  reconnect reuses the SAME session_id. So a live tool-timeout offer
   *  that survives a drop can no longer trust its `retry_id`: the backend
   *  has no memory of it, "Try again" would hit a silent guard, and a card
   *  already in RETRYING would spin forever waiting for an outcome that
   *  can now never arrive. Same trust boundary as a page reload (see
   *  lib/tool-timeout-restore.ts's identical reasoning for a RESTORED
   *  offer) — the frontend resets per-connection because the backend's
   *  guarantee is per-connection. No-op when there's no live offer. */
  onSocketReconnected: () => void;
  /** OpenAI rate-limited a response and the backend scheduled (`retry_in`
   *  seconds) or exhausted (`retry_in: null`) a delayed retry
   *  (rate_limit_wait, FB32) — surfaces an explicit MODEL_BUSY notice so a
   *  silent multi-second gap doesn't read as "broken". No-op outside a call
   *  (see useCallMode.handleModelBusy) — TEXT mode's "Working…" timeline
   *  already covers the wait. */
  onModelBusy: (retryIn: number | null) => void;
  /** Clears an active MODEL_BUSY notice — called the moment the backend
   *  reports agent output has started (turn_state: responding), the
   *  cleanest existing "Sara actually responded" signal. No-op outside a
   *  call / when no MODEL_BUSY notice is showing. */
  onModelResponded: () => void;
  /** Turn 15 — null outside a rate-limit pause; 'waiting' while a retry is
   *  scheduled, 'exhausted' once the budget ran out. Drives the orb's
   *  dim-and-rest treatment and the status cluster's "Resting a moment"
   *  override — see useCallMode.handleModelBusy/askAgain. */
  modelBusy: 'waiting' | 'exhausted' | null;
  /** Turn 15e "the exhale" — a snapshot of the MODEL_BUSY notice that just
   *  retracted because Sara responded, held for the release-downward exit's
   *  duration. CallScreen renders `callNotice ?? releasingNotice` so the
   *  notice can animate out instead of popping away the instant `callNotice`
   *  clears. Null the rest of the time. */
  releasingNotice: CallNotice | null;
  /** Turn 15e — bumped once per MODEL_BUSY recovery; forwarded to
   *  CallOrb's `releaseSignal` prop to replay its one-shot ripple. */
  orbReleaseSignal: number;
  /** Audioinput devices enumerated after mic permission is granted (labels
   *  only populate post-grant) — feeds the mic-switcher device menu.
   *  Refreshed on call start and on every native 'devicechange' while a
   *  call is live. See useCallMode.ts. */
  audioInputs: MediaDeviceInfo[];
  /** The FULL enumerateDevices() list (inputs AND outputs) — mic-selection's
   *  headset classification (micDeviceGroup) needs audiooutput groupIds. */
  audioDevices: MediaDeviceInfo[];
  /** deviceId of the currently-capturing track, or null when no call is
   *  active / the chain hasn't reported settings yet. */
  activeMicId: string | null;
  /** User-initiated mic switch (the device-menu escape hatch — Meet parity).
   *  No-op if no call is active or `deviceId` matches the active track. */
  switchMic: (deviceId: string) => Promise<void>;
  /** In-call notification (Turn-12 12b), rendered by voice/call-notice.tsx
   *  inside CallScreen. Null when nothing to show. Produced/lifecycled by
   *  useCallMode (success auto-clears; cleared on stop and when a fresh
   *  capture chain goes live). */
  callNotice: CallNotice | null;
  dismissCallNotice: () => void;
  /** Index into `messages` at the moment the current/last call started
   *  (v3 CallScreen transcript rail). `messages.slice(callStartMessageIndexRef
   *  .current)` isolates "this call's" turns from anything sent before the
   *  call began. Set by the wrapped `toggleCall` below, synchronously before
   *  the call actually starts. */
  callStartMessageIndexRef: React.MutableRefObject<number>;

  // Voice-call pinned card HISTORY (design turn 14, building on turn 11 part
  // B). `pinnedCard` is the front entry's card (or null) — kept for
  // consumers that only ever read the front pin (desktop's presentation is
  // unchanged by turn 14). `pinHistory` is the full capped stack (newest
  // first, MAX_PINS) for the mobile peekable-stack UI (14c). Entries are
  // pushed by useAssistantConnection's stashOrInsertToolCard VOICE branch on
  // a fresh find_courses/browse_aol_content result; the WHOLE history is
  // cleared when the call starts AND when it stops (see the wrapped
  // toggleCall below, via clearPins) — a pin belongs to the call it was
  // pinned during. `dismissPin` (the card's x affordance) pops only the
  // FRONT pin, promoting the next most recent; `bringPinToFront` is the
  // stack's "tap an older row" promotion (14c).
  pinnedCard: PinnedCard | null;
  pinHistory: PinEntry[];
  pinCard: (card: PinnedCard) => void;
  dismissPin: () => void;
  bringPinToFront: (id: string) => void;
  /** Call-lifecycle reset — clears the WHOLE pin history, unlike
   *  dismissPin's front-only pop. See the wrapped toggleCall below. */
  clearPins: () => void;

  // Voice recording
  isRecording: boolean;
  handleVoiceRecording: () => Promise<void>;

  // Refs
  wsRef: React.MutableRefObject<WebSocket | null>;
  chatLayoutRef: React.RefObject<ChatLayoutRef | null>;
  /** Imperative focus handle of the composer (PromptInputBar). */
  promptInputRef: React.RefObject<PromptInputBarRef | null>;
  sessionIdRef: React.MutableRefObject<string>;
  /** Inbox of the connection machine (src/machines/connection). The driver
   *  hook (useAssistantConnection) registers its dispatch here on mount;
   *  sendText/retryMessage feed user intent through it. Context-as-bus,
   *  same pattern as wsRef. Null until the driver mounts. */
  connectionDispatchRef: React.MutableRefObject<
    ((event: ConnEvent) => void) | null
  >;

  // Functions
  sendText: (text: string) => void;
  /** Re-attempt delivery of a failed user message (same id → the backend
   *  dedups, so a retry can never double-process). */
  retryMessage: (id: string) => void;
  /** Single reset point for "new chat": new durable conversation + session
   *  (the machine reconnects immediately), hero headline back to default,
   *  landing screen via the session-change effect. */
  startNewChat: () => void;
  cleanupResources: () => void;
}

const ChatCtx = createContext<ChatContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  // External store
  const sessionId = useConversationStore((s) => s.sessionId);
  // The tool-timeout flow's support-request submissions need the DURABLE
  // conversation identity (not sessionId) — see useToolTimeoutFlow's own
  // `conversationId` option and lib/api/support-requests.ts.
  const conversationId = useConversationStore((s) => s.conversationId);

  // Core refs
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string>(sessionId);
  const chatLayoutRef = useRef<ChatLayoutRef>(null);
  const promptInputRef = useRef<PromptInputBarRef>(null);
  const connectionDispatchRef = useRef<((event: ConnEvent) => void) | null>(
    null
  );
  // v3 CallScreen transcript rail: which messages belong to the current/last
  // call. Set by the wrapped toggleCall below.
  const callStartMessageIndexRef = useRef(0);

  // Compose hooks
  const thinking = useThinkingTicker();
  const messageState = useMessageManagement();
  const viz = useAudioVisualization(thinking.isThinkingRef);
  const playback = useAudioPlayback(viz.registerAgentAnalyser);
  const call = useCallMode({
    wsRef,
    registerUserAnalyser: viz.registerUserAnalyser,
    resetToIdle: viz.resetToIdle,
    // stopCall owns "ending a call silences Sara" — see the option doc in
    // useCallMode.ts.
    clearPlayback: playback.clearPlayback,
    // startCall unlocks Sara's <audio> sink inside the tap's activation —
    // see the option doc in useCallMode.ts.
    primePlayback: playback.primePlayback,
    // [rtc-spike] Same sessionIdRef useAssistantConnection.ts reads to
    // build the `/ws/{session_id}` URL — see useCallMode's option doc.
    sessionIdRef,
    // [rtc-spike] Same registerAgentAnalyser useAudioPlayback (above)
    // receives — see useCallMode's option doc for why sharing it is safe.
    registerAgentAnalyser: viz.registerAgentAnalyser,
  });
  const handoffState = useHandoff();
  const pinnedCardState = usePinnedCard();

  // UI state — declared before the useCallback-wrapped functions below,
  // several of which close over these setters/values in their dependency
  // arrays (evaluated eagerly every render, unlike the closures' bodies).
  const [uiState, setUIState] = useState<UIState>(UIState.LANDING);
  const [inputValue, setInputValue] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.CONNECTING
  );
  // Backend turn activity — null until the first TURN_STATE arrives (old
  // backends never send one, so null also gates the legacy derivation).
  const [turnState, setTurnState] = useState<TurnState | null>(null);
  const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
  // Session-recovery tool-timeout offer — see the interface field's doc
  // comment above.
  const [toolTimeoutOffer, setToolTimeoutOffer] =
    useState<ToolTimeoutOfferState | null>(null);
  // Session-recovery retry outcome — see the interface field's doc comment
  // above.
  const [toolRetryOutcome, setToolRetryOutcome] =
    useState<ToolRetryOutcomeState | null>(null);
  // Session-recovery (reconnect reset): the retryId a reconnect invalidated
  // WHILE it was still the live offer's — see onSocketReconnected below and
  // the `onSocketReconnected` interface field's doc comment for the full
  // "why". Read via retryId comparison, not object identity, so it survives
  // this SAME incident's offer object being re-created by unrelated
  // updates; naturally stops applying the moment a genuinely NEW incident
  // (a different retryId) arrives — no separate clear step needed.
  const [staleRetryId, setStaleRetryId] = useState<string | null>(null);
  // Kept in sync so onSocketReconnected (a stable useCallback) can read the
  // CURRENT live offer without closing over a stale one.
  const toolTimeoutOfferRef = useRef(toolTimeoutOffer);
  useEffect(() => {
    toolTimeoutOfferRef.current = toolTimeoutOffer;
  }, [toolTimeoutOffer]);
  const onSocketReconnected = useCallback(() => {
    const live = toolTimeoutOfferRef.current;
    if (live) setStaleRetryId(live.retryId);
  }, []);

  // Wraps call.toggleCall so the v3 CallScreen transcript rail
  // (voice/transcript-rail.tsx) knows exactly which messages belong to THIS
  // call. Captured synchronously, BEFORE the async startCall resolves, so a
  // rapid stop→restart can't race a stale index left over from the previous
  // call. ALSO clears the WHOLE pinned-card history on both transitions
  // (call start AND call stop) via clearPins — a pin belongs to the call it
  // was pinned during; it must not survive into the next call or linger
  // after this one ends. This is a full reset, not the front-only pop
  // dismissPin does for the card's own x affordance.
  //
  // [FB34] Branches on `call.callPhase !== 'idle'`, NOT `call.isCalling` —
  // isCalling is still false throughout `connecting`, so the old
  // isCalling-only check took this START branch on a second tap mid-connect
  // (re-clearing pins / resetting the transcript index) instead of ending
  // the in-flight attempt; useCallMode's own toggleCall has the matching
  // fix for its internal start-vs-stop branch.
  const toggleCall = useCallback(async () => {
    if (call.callPhase === 'idle') {
      callStartMessageIndexRef.current = messageState.messages.length;
      // A voice call IS a conversation — land on the chat surface now so the
      // transcript accumulated during the call is visible when it ends. A
      // call started from LANDING otherwise dropped back onto the starter
      // screen with the voice turns hidden underneath (sendText has the same
      // LANDING→CHAT flip for text).
      setUIState(UIState.CHAT);
    }
    pinnedCardState.clearPins();
    await call.toggleCall();
    // exhaustive-deps wants the whole `call`/`pinnedCardState` objects because
    // it can't verify a CALLED member (`call.toggleCall()`,
    // `pinnedCardState.clearPins()`) is stable — but useCallMode/usePinnedCard
    // return a fresh object every render while wrapping each function in its
    // OWN useCallback, so the specific leaves below are the actually-stable
    // values; depending on the parent objects would recompute this every
    // render and defeat the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    call.callPhase,
    call.toggleCall,
    messageState.messages,
    pinnedCardState.clearPins,
  ]);

  // [FB36] Call end settles the chat to idle. A call torn down mid-turn
  // kills the in-flight response with it (the sideband dies; the rebuilt
  // text session doesn't resume it) and nobody re-emits a terminal
  // turn_state on the old contract — so both typing-dots legs latched on
  // forever: the last backend turn_state (THINKING from the final voice
  // transcription's persist) and the awaiting derivation (a trailing live
  // user message that will never be answered). Clear the phase locally and
  // stamp the orphaned message `abandoned` at this ONE chokepoint — every
  // end-of-call path (End tap, failure dismiss, terminal notify) funnels
  // through the phase machine's return to 'idle'. The backend's own
  // turn_state:idle on detach (same fix, server side) is the authoritative
  // belt; this covers old backends and a socket drop during hangup. The
  // prev-ref guard skips the initial mount (phase starts at 'idle').
  const prevCallPhaseRef = useRef(call.callPhase);
  // [FB36 review] Settle ONLY when the ended call actually went LIVE — a
  // pre-live failure (mic denied / timeout / network, then dismissed) never
  // swapped the backend session, so an in-flight TEXT turn from before the
  // attempt is still healthy: stamping it abandoned + nulling turnState
  // would kill the dots on a reply that IS coming.
  const callWentLiveRef = useRef(false);
  useEffect(() => {
    if (call.callPhase === 'live') callWentLiveRef.current = true;
    const prev = prevCallPhaseRef.current;
    prevCallPhaseRef.current = call.callPhase;
    if (prev === 'idle' || call.callPhase !== 'idle') return;
    const wentLive = callWentLiveRef.current;
    callWentLiveRef.current = false;
    if (!wentLive) return;
    setTurnState(null);
    messageState.setMessages(settleAbandonedTurn);
    // Mirror the WS idle handler's other half: a reply cut off mid-stream by
    // the hangup must not keep a forever-blinking streaming caret.
    messageState.finalizeStreaming();
    // setMessages/finalizeStreaming are the stable leaves of messageState —
    // see toggleCall's note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    call.callPhase,
    messageState.setMessages,
    messageState.finalizeStreaming,
  ]);

  // Send text message. UNCONDITIONAL on socket state — the old readyState
  // guard here silently dropped submits made while the socket was still
  // connecting (#96: no chat transition, no bubble, message lost). Now the
  // UI always honors the intent and the connection machine owns delivery:
  // send-now when open, queue-until-connected otherwise, revive a
  // terminally-failed connection.
  const sendText = useCallback(
    (text: string) => {
      const trimmedText = text.trim();
      if (!trimmedText) return;

      // Pure decision: is this reply headed to Sara (text) or the live
      // HubSpot rep (agent_text)? See send-routing.ts — extracted so the
      // routing logic is unit-tested without React.
      const frame = resolveSendFrame(uiState, handoffState.handoff);

      // UIState reflects INTENT, never transport state. Only the Sara path
      // ever needs the LANDING→CHAT flip — a handoff can only be active once
      // the conversation is already underway.
      if (
        frame.frameKind === OutboundFrameKind.TEXT &&
        uiState === UIState.LANDING
      ) {
        setUIState(UIState.CHAT);
      }

      // One identity end-to-end: React key, ack correlation
      // (metadata.client_msg_id on the wire), and the backend's at-most-once
      // dedup key. Minted here so the optimistic bubble and the outbox item
      // can never drift apart.
      const id = uuidv4();
      // createdAt: turn-start anchor for the timeline's live "Worked for Xs"
      // measurement (ThinkingBlock) — mirrors the created_at restored rows carry.
      messageState.setMessages((prev) => [
        ...prev,
        { role: 'user', content: trimmedText, id, createdAt: Date.now() },
      ]);

      // The machine appends the delivery status (queued/sent/acked/failed)
      // onto the bubble via UPDATE_MESSAGE_STATUS effects — functional
      // setState ordering guarantees the append above lands first.
      connectionDispatchRef.current?.(
        frame.frameKind === OutboundFrameKind.AGENT_TEXT
          ? {
              type: 'SEND',
              id,
              text: trimmedText,
              frameKind: frame.frameKind,
              handoffId: frame.handoffId,
            }
          : { type: 'SEND', id, text: trimmedText }
      );
      setInputValue('');
    },
    // Same false-positive as toggleCall above: `messageState.setMessages(...)`
    // is a called member, so exhaustive-deps wants the whole (per-render-
    // fresh) `messageState` object instead of the actually-stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uiState, handoffState.handoff, messageState.setMessages]
  );

  // Re-attempt a failed delivery. Reuses the ORIGINAL message id so the
  // backend's client_msg_id dedup makes retries idempotent (a frame the
  // server actually received is re-acked, never re-processed). Same
  // frame-routing decision as sendText — a failed handoff reply retries as
  // agent_text, not a plain Sara text frame.
  const retryMessage = useCallback(
    (id: string) => {
      const msg = messageState.messages.find((m) => m.id === id);
      if (!msg?.content) return;
      const frame = resolveSendFrame(uiState, handoffState.handoff);
      connectionDispatchRef.current?.(
        frame.frameKind === OutboundFrameKind.AGENT_TEXT
          ? {
              type: 'RETRY_MESSAGE',
              id,
              text: msg.content,
              frameKind: frame.frameKind,
              handoffId: frame.handoffId,
            }
          : { type: 'RETRY_MESSAGE', id, text: msg.content }
      );
    },
    // Same false-positive as sendText above: `messageState.messages.find(...)`
    // is a called member, so exhaustive-deps wants the whole (per-render-
    // fresh) `messageState` object instead of the actually-stable `.messages`
    // array reference already listed below.
    [messageState.messages, uiState, handoffState.handoff]
  );

  // User-initiated handoff end (the banner's "End chat" affordance). Sent
  // directly on the socket — like call audio, this bypasses the outbox by
  // design (a one-shot control frame has no ack/retry semantics to reuse).
  // Does NOT optimistically flip handoff.phase: the server's HANDOFF_ENDED
  // event is the sole driver of that transition (see useAssistantConnection),
  // so a dropped end_handoff frame can't leave the UI showing "ended" while
  // the backend still thinks the handoff is live.
  const sendEndHandoff = useCallback(() => {
    if (!handoffState.handoff) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: WSEventType.END_HANDOFF,
        handoff_id: handoffState.handoff.handoffId,
      })
    );
  }, [handoffState.handoff]);

  // Pre-handoff contact-form submission (Decision #17,
  // 2026-07-hubspot-native-handoff.md). Sent directly on the socket — like
  // sendEndHandoff/call audio, this bypasses the outbox by design: it's a
  // one-shot form submit with no Message bubble to attach SendStatus to, not
  // a chat turn needing the ack-retry lifecycle built for text/agent_text
  // frames. ContactInfoCard drives its own local "submitting" UI purely off
  // the NEXT event that lands (HANDOFF_STARTED or a re-emitted
  // CONTACT_INFO_REQUESTED) rather than an ack — see its useEffect on
  // `fields`. `phone` is omitted from the wire payload when blank
  // (JSON.stringify drops undefined keys) rather than sent as `""`, matching
  // the backend's Optional[str] = None field.
  const submitContactInfo = useCallback(
    (values: { name: string; email: string; phone?: string }) => {
      if (!handoffState.handoff) return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: WSEventType.CONTACT_INFO,
          handoff_id: handoffState.handoff.handoffId,
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
        })
      );
    },
    [handoffState.handoff]
  );

  // Session-recovery "Try again" (Decision C9/D3). Sent directly on the
  // socket — like submitContactInfo/sendEndHandoff above, this bypasses the
  // outbox by design: a one-shot control frame with no ack/retry lifecycle
  // to attach to a Message. No `handoff` guard (unlike the two callbacks
  // above) — this flow is independent of live-agent handoff and normally has
  // none. Carries `retry_id` (the backend rejects the frame outright if it
  // doesn't match the pending incident — see RetryToolCallMessage's
  // docstring) and returns whether the frame was actually SENT — see this
  // field's interface doc comment (Finding 5): a caller must not enter
  // RETRYING off a send that never happened.
  const retryToolCall = useCallback((retryId: string): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(
      JSON.stringify({ type: WSEventType.RETRY_TOOL_CALL, retry_id: retryId })
    );
    return true;
  }, []);

  // ── Tool-timeout recovery flow: ONE shared instance (bug fix — see
  // useToolTimeoutFlow's own header comment for the divergence this
  // replaces). "Live wins over restored" mirrors the pre-existing
  // page.tsx/CallScreen resolution exactly (now the ONLY place it happens),
  // and ALSO forces `retryAvailable: false` on a restored-only offer — see
  // resolveEffectiveToolTimeoutOffer's own doc comment for why a restored
  // retryId can never actually work. `knownContact` is the same "last
  // contact record on file this conversation" scan those two call sites
  // used to each run themselves. ──
  const effectiveToolTimeoutOffer = useMemo(() => {
    const resolved = resolveEffectiveToolTimeoutOffer(
      toolTimeoutOffer,
      messageState.messages
    );
    // Session-recovery (reconnect reset): a SECOND, independent reason a
    // retry can be unusable — see onSocketReconnected's doc comment. Kept
    // as its own pass rather than folded into resolveEffectiveToolTimeoutOffer
    // (that function only ever sees "live vs. restored"; it has no idea a
    // connection dropped mid-incident) — composed here instead so both
    // reasons converge on the SAME `retryAvailable: false` contract every
    // downstream reader (the hook, both card surfaces) already honors.
    if (resolved && resolved.retryId === staleRetryId) {
      return { ...resolved, retryAvailable: false };
    }
    return resolved;
  }, [toolTimeoutOffer, messageState.messages, staleRetryId]);
  // Session-recovery (reconnect reset): true only on the specific incident
  // a reconnect just invalidated — drives useToolTimeoutFlow's own
  // RETRYING -> OFFER/FAILED snap-back (stopping a spinner that can now
  // never resolve). Derived from the SAME comparison as the retryAvailable
  // override above, not a second source of truth.
  const retryStaleAfterReconnect =
    effectiveToolTimeoutOffer?.retryId === staleRetryId;
  const knownContact = useMemo(
    () => resolveKnownContact(messageState.messages),
    [messageState.messages]
  );
  const toolTimeoutFlowResult = useToolTimeoutFlow({
    toolName: effectiveToolTimeoutOffer?.toolName ?? '',
    attempt: effectiveToolTimeoutOffer?.attempt ?? 1,
    retryAvailable: effectiveToolTimeoutOffer?.retryAvailable ?? true,
    retryId: effectiveToolTimeoutOffer?.retryId ?? '',
    conversationId,
    messages: messageState.messages,
    onRetry: retryToolCall,
    retryOutcome: toolRetryOutcome,
    retryStaleAfterReconnect,
    nameOnFile: knownContact?.name,
  });
  const toolTimeoutFlow: ToolTimeoutFlowContextValue | null = useMemo(() => {
    if (!effectiveToolTimeoutOffer) return null;
    return {
      ...toolTimeoutFlowResult,
      offer: effectiveToolTimeoutOffer,
      phoneOnFile: knownContact?.phone ?? undefined,
    };
  }, [effectiveToolTimeoutOffer, toolTimeoutFlowResult, knownContact]);

  // Single reset point for "new chat" (popover/drawer "New" buttons, and any
  // future entry point). The store reset mints a new conversationId AND
  // sessionId — the sessionId change drives the machine's immediate
  // reconnect and lands on LANDING (expectingHistory=false). The headline
  // reset is why this lives here: a topic click mutates it, and without the
  // reset a new chat kept showing the previous topic's headline.
  const startNewChat = useCallback(() => {
    useConversationStore.getState().startNewConversation();
    setHeadline(DEFAULT_HEADLINE);
    // Fresh chat is immediately typeable — focus after the empty surface
    // renders (rAF: the composer may remount with the UI state change).
    requestAnimationFrame(() => promptInputRef.current?.focus());
  }, []);

  // Voice recording (uses sendText callback; sendText owns the
  // LANDING→CHAT flip and queues if the socket isn't open yet)
  const recording = useVoiceRecording({ onTranscript: sendText });

  // Cleanup all resources
  //
  // [FB34] `call.stopCallIfActive()` (phase-aware), not the raw
  // `isCallingRef.current` check + `stopCall()` this used to be —
  // isCallingRef misses `connecting`/`failed`, so e.g. "New chat" mid-connect
  // (this fires on unmount/session-change) used to leave the half-built call
  // running silently in the background instead of tearing it down.
  const cleanupResources = useCallback(() => {
    call.stopCallIfActive();
    if (recording.isRecording && recording.mediaRecorderRef.current) {
      recording.mediaRecorderRef.current.stop();
    }
    // stopCallIfActive above already clears playback for the in-call path;
    // this unconditional clear stays for resets arriving OUTSIDE a call (a
    // late buffer tail after stopCall already ran, or any future non-call
    // playback) — clearPlayback is idempotent, so the overlap is free.
    playback.clearPlayback();
    if (thinking.isThinkingRef.current) {
      thinking.isThinkingRef.current = false;
      thinking.stopThinkingTicker();
    }
    // Same false-positive as toggleCall/sendText above — `call.
    // stopCallIfActive()`, `playback.clearPlayback()`,
    // `thinking.stopThinkingTicker()` are called members, so exhaustive-deps
    // wants their (per-render-fresh) parent objects instead of the
    // actually-stable functions listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    call.stopCallIfActive,
    recording.isRecording,
    recording.mediaRecorderRef,
    playback.clearPlayback,
    thinking.isThinkingRef,
    thinking.stopThinkingTicker,
  ]);

  // Every consumer of useChatContext() re-rendered on every ChatProvider
  // render without this — harmless most of the time, but during a call
  // captions/levels used to update at audio rate (see
  // useAudioVisualization.ts), which meant the entire assistant UI
  // re-rendered dozens of times a second. The level-engine rewrite already
  // removed level from React state entirely; this memo is the other half —
  // stabilizing the `value` object itself so an unrelated field changing
  // doesn't force-render everything, and so a ChatProvider render triggered
  // by, say, `inputValue` doesn't recompute or reflow anything that depends
  // only on `messages`.
  const value: ChatContextValue = useMemo(
    () => ({
      // Messages
      messages: messageState.messages,
      setMessages: messageState.setMessages,
      insertAssistantMessage: messageState.insertAssistantMessage,
      insertToolResult: messageState.insertToolResult,
      insertHumanAgentMessage: messageState.insertHumanAgentMessage,
      insertContactSummary: messageState.insertContactSummary,
      appendAssistantDelta: messageState.appendAssistantDelta,
      dropStreamBuffer: messageState.dropStreamBuffer,
      finalizeStreaming: messageState.finalizeStreaming,
      classifyMessages: messageState.classifyMessages,
      setMessageDbId: messageState.setMessageDbId,

      // UI state
      uiState,
      setUIState,
      inputValue,
      setInputValue,
      connectionStatus,
      setConnectionStatus,
      turnState,
      setTurnState,
      headline,
      setHeadline,

      // Live-agent handoff
      handoff: handoffState.handoff,
      contactInfoRequested: handoffState.contactInfoRequested,
      startHandoff: handoffState.startHandoff,
      agentJoined: handoffState.agentJoined,
      agentMessageReceived: handoffState.agentMessageReceived,
      markHandoffEnded: handoffState.markEnded,
      markHandoffTimedOut: handoffState.markTimedOut,
      clearHandoff: handoffState.clearHandoff,
      sendEndHandoff,
      submitContactInfo,

      // Session-recovery: tool-timeout offer
      toolTimeoutOffer,
      setToolTimeoutOffer,
      toolRetryOutcome,
      setToolRetryOutcome,
      retryToolCall,
      toolTimeoutFlow,

      // Visualization
      vizMode: viz.vizMode,
      setVizMode: viz.setVizMode,
      audioLevelsRef: viz.levelsRef,
      resetToIdle: viz.resetToIdle,

      // Thinking
      isThinkingRef: thinking.isThinkingRef,
      startThinkingTicker: thinking.startThinkingTicker,
      stopThinkingTicker: thinking.stopThinkingTicker,

      // Audio playback
      enqueueAudio: playback.enqueueAudio,
      clearPlayback: playback.clearPlayback,

      // Call mode
      isCalling: call.isCalling,
      isCallingRef: call.isCallingRef,
      callPhase: call.callPhase,
      callFailureReason: call.callFailureReason,
      waitingOnMicPermission: call.waitingOnMicPermission,
      retryCall: call.retryCall,
      toggleCall,
      stopCall: call.stopCall,
      stopCallIfActive: call.stopCallIfActive,
      showCC: call.showCC,
      setShowCC: call.setShowCC,
      isMuted: call.isMuted,
      toggleMute: call.toggleMute,
      callStartedAtRef: call.callStartedAtRef,
      onCallConnectionDrop: call.handleConnectionDrop,
      onCallConnectionRestored: call.handleConnectionRestored,
      onSocketReconnected,
      onModelBusy: call.handleModelBusy,
      onModelResponded: call.handleModelResponded,
      modelBusy: call.modelBusy,
      releasingNotice: call.releasingNotice,
      orbReleaseSignal: call.orbReleaseSignal,
      audioInputs: call.audioInputs,
      audioDevices: call.audioDevices,
      activeMicId: call.activeMicId,
      switchMic: call.switchMic,
      callNotice: call.callNotice,
      dismissCallNotice: call.dismissCallNotice,
      callStartMessageIndexRef,

      // Voice-call pinned card history
      pinnedCard: pinnedCardState.pinnedCard,
      pinHistory: pinnedCardState.pinHistory,
      pinCard: pinnedCardState.pinCard,
      dismissPin: pinnedCardState.dismissPin,
      bringPinToFront: pinnedCardState.bringPinToFront,
      clearPins: pinnedCardState.clearPins,

      // Voice recording
      isRecording: recording.isRecording,
      handleVoiceRecording: recording.toggleRecording,

      // Refs
      wsRef,
      chatLayoutRef,
      promptInputRef,
      sessionIdRef,
      connectionDispatchRef,

      // Functions
      sendText,
      retryMessage,
      startNewChat,
      cleanupResources,
    }),
    [
      messageState.messages,
      messageState.setMessages,
      messageState.insertAssistantMessage,
      messageState.insertToolResult,
      messageState.insertHumanAgentMessage,
      messageState.insertContactSummary,
      messageState.appendAssistantDelta,
      messageState.dropStreamBuffer,
      messageState.finalizeStreaming,
      messageState.classifyMessages,
      messageState.setMessageDbId,
      uiState,
      inputValue,
      connectionStatus,
      turnState,
      headline,
      handoffState.handoff,
      handoffState.contactInfoRequested,
      handoffState.startHandoff,
      handoffState.agentJoined,
      handoffState.agentMessageReceived,
      handoffState.markEnded,
      handoffState.markTimedOut,
      handoffState.clearHandoff,
      sendEndHandoff,
      submitContactInfo,
      toolTimeoutOffer,
      setToolTimeoutOffer,
      toolRetryOutcome,
      setToolRetryOutcome,
      retryToolCall,
      toolTimeoutFlow,
      viz.vizMode,
      viz.setVizMode,
      viz.levelsRef,
      viz.resetToIdle,
      thinking.isThinkingRef,
      thinking.startThinkingTicker,
      thinking.stopThinkingTicker,
      playback.enqueueAudio,
      playback.clearPlayback,
      call.isCalling,
      call.isCallingRef,
      call.callPhase,
      call.callFailureReason,
      call.waitingOnMicPermission,
      call.retryCall,
      toggleCall,
      call.stopCall,
      call.stopCallIfActive,
      call.showCC,
      call.setShowCC,
      call.isMuted,
      call.toggleMute,
      call.callStartedAtRef,
      call.handleConnectionDrop,
      call.handleConnectionRestored,
      onSocketReconnected,
      call.handleModelBusy,
      call.handleModelResponded,
      call.modelBusy,
      call.releasingNotice,
      call.orbReleaseSignal,
      call.audioInputs,
      call.audioDevices,
      call.activeMicId,
      call.switchMic,
      call.callNotice,
      call.dismissCallNotice,
      pinnedCardState.pinnedCard,
      pinnedCardState.pinHistory,
      pinnedCardState.pinCard,
      pinnedCardState.dismissPin,
      pinnedCardState.bringPinToFront,
      pinnedCardState.clearPins,
      recording.isRecording,
      recording.toggleRecording,
      sendText,
      retryMessage,
      startNewChat,
      cleanupResources,
    ]
  );

  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

// ─── Consumer hook ──────────────────────────────────────────────

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatCtx);
  if (!ctx) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return ctx;
}
